import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { PermissionEvaluationInput, PermissionMap, PermissionSnapshot, PermissionStatus, PermissionSurface } from '../types/permissions'
import { useAuth } from './user.context'
import { permissionService } from '../api'
import { implementedAuditedEntities, secondaryReferenceEntities } from '../permissions/catalog'
import { evaluatePermissionSurface } from '../permissions/evaluate'
import { mergePermissionMaps, normalizePermissionMap } from '../permissions/normalize'
import type { PermissionContextValue } from '../permissions/types'
import { isS3AvailableRegion } from '../utils/region'

const createInitialSnapshot = (clientId: string | number | null = null): PermissionSnapshot => ({
  clientId,
  status: 'unknown',
  appMenuFeatures: [],
  entityPermissions: {},
  loadedAt: null,
  sourceNotes: [],
})

const normalizeClientScopeId = (clientId: string | number | null | undefined): string | null => {
  if (clientId === null || clientId === undefined) {
    return null
  }

  return String(clientId)
}

const appendNote = (notes: string[], note?: string): string[] => {
  if (!note) {
    return notes
  }

  return Array.from(new Set([...notes, note])).slice(-25)
}

const excludedBootstrapEndpoints = new Set(['/backend/api/permissionsset/role_permissions'])

const permissionBootstrapSources = [...implementedAuditedEntities, ...secondaryReferenceEntities].flatMap((entry) =>
  entry.apiEvidence
    .filter(
      (endpoint) =>
        endpoint.includes('/permissions') &&
        !endpoint.includes('{') &&
        !excludedBootstrapEndpoints.has(endpoint),
    )
    .map((endpoint) => ({ entityKey: entry.entityKey, endpoint })),
)

const PermissionContext = React.createContext<PermissionContextValue | undefined>(undefined)

export const PermissionProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { user } = useAuth()
  const activeClientId = user?.user?.active_client ?? null
  const previousClientIdRef = useRef<string | number | null | undefined>(undefined)
  const activeClientIdRef = useRef<string | number | null>(activeClientId)
  const [snapshot, setSnapshot] = useState<PermissionSnapshot>(() => createInitialSnapshot(activeClientId))
  const [isAppMenuLoaded, setIsAppMenuLoaded] = useState(false)
  const [isBootstrapLoaded, setIsBootstrapLoaded] = useState(false)

  activeClientIdRef.current = activeClientId

  const isCurrentClientScope = useCallback(
    (scopedClientId: string | number | null | undefined) =>
      normalizeClientScopeId(scopedClientId) === normalizeClientScopeId(activeClientIdRef.current),
    [],
  )

  const setPermissionStatus = useCallback((status: PermissionStatus, note?: string) => {
    const scopedClientId = activeClientId

    if (!isCurrentClientScope(scopedClientId)) {
      return
    }

    if (status === 'loading') {
      setIsAppMenuLoaded(false)
      setIsBootstrapLoaded(!scopedClientId)
    }

    setSnapshot((current) => ({
      ...current,
      status,
      loadedAt: status === 'ready' ? new Date().toISOString() : current.loadedAt,
      sourceNotes: appendNote(current.sourceNotes, note),
    }))
  }, [activeClientId, isCurrentClientScope])

  const setAppMenuFeatures = useCallback((features: string[], note?: string) => {
    const scopedClientId = activeClientId

    if (!isCurrentClientScope(scopedClientId)) {
      return
    }

    setIsAppMenuLoaded(true)

    setSnapshot((current) => ({
      ...current,
      appMenuFeatures: Array.from(new Set(features.filter(Boolean))),
      sourceNotes: appendNote(current.sourceNotes, note),
    }))
  }, [activeClientId, isCurrentClientScope])

  const setEntityPermissions = useCallback((entityKey: string, permissions: PermissionMap, note?: string) => {
    const scopedClientId = activeClientId

    if (!isCurrentClientScope(scopedClientId)) {
      return { ...permissions }
    }

    const normalizedPermissions = { ...permissions }

    setSnapshot((current) => ({
      ...current,
      entityPermissions: {
        ...current.entityPermissions,
        [entityKey]: normalizedPermissions,
      },
      loadedAt: current.status === 'ready' ? new Date().toISOString() : current.loadedAt,
      sourceNotes: appendNote(current.sourceNotes, note),
    }))

    return normalizedPermissions
  }, [activeClientId, isCurrentClientScope])

  const mergeEntityPermissions = useCallback((entityKey: string, source: unknown, note?: string) => {
    const scopedClientId = activeClientId
    const incomingPermissions = normalizePermissionMap(source)

    if (!isCurrentClientScope(scopedClientId)) {
      return incomingPermissions
    }

    setSnapshot((current) => ({
      ...current,
      entityPermissions: {
        ...current.entityPermissions,
        [entityKey]: mergePermissionMaps(current.entityPermissions[entityKey] || {}, incomingPermissions),
      },
      loadedAt: current.status === 'ready' ? new Date().toISOString() : current.loadedAt,
      sourceNotes: appendNote(current.sourceNotes, note),
    }))

    return incomingPermissions
  }, [activeClientId, isCurrentClientScope])

  const getEntityPermissions = useCallback(
    (entityKey: string) => snapshot.entityPermissions[entityKey] || {},
    [snapshot.entityPermissions],
  )

  const clearClientScope = useCallback((nextClientId?: string | number | null, note?: string) => {
    setIsAppMenuLoaded(false)
    setIsBootstrapLoaded(!nextClientId)

    setSnapshot((current) => ({
      clientId: nextClientId ?? null,
      status: nextClientId ? 'stale' : 'unknown',
      appMenuFeatures: [],
      entityPermissions: {},
      loadedAt: null,
      sourceNotes: appendNote(current.sourceNotes, note),
    }))
  }, [])

  useEffect(() => {
    if (!user?.user) {
      previousClientIdRef.current = undefined
      setIsAppMenuLoaded(false)
      setIsBootstrapLoaded(false)
      setSnapshot(createInitialSnapshot())
      return
    }

    if (previousClientIdRef.current === undefined) {
      previousClientIdRef.current = activeClientId
      setSnapshot((current) => ({
        ...current,
        clientId: activeClientId,
      }))
      return
    }

    if (previousClientIdRef.current !== activeClientId) {
      previousClientIdRef.current = activeClientId
      clearClientScope(activeClientId, activeClientId ? `Active client changed to ${activeClientId}` : 'Active client cleared')
    }
  }, [activeClientId, clearClientScope, user?.user])

  useEffect(() => {
    if (!user?.user) {
      return
    }

    if (!activeClientId) {
      setIsBootstrapLoaded(true)
      return
    }

    let isCancelled = false

    setIsBootstrapLoaded(false)

    const availablePermissionBootstrapSources = permissionBootstrapSources.filter(
      ({ endpoint }) =>
        !endpoint.includes('/s3-tokens/') ||
        isS3AvailableRegion(localStorage.getItem('region_ocp_zone')),
    )

    void Promise.allSettled(
      availablePermissionBootstrapSources.map(async ({ entityKey, endpoint }) => {
        const response = await permissionService.getDedicatedPermissions(endpoint)

        return { entityKey, endpoint, data: response.data }
      }),
    ).then((results) => {
      if (isCancelled) {
        return
      }

      results.forEach((result) => {
        if (result.status !== 'fulfilled') {
          return
        }

        mergeEntityPermissions(
          result.value.entityKey,
          result.value.data,
          `Loaded ${result.value.endpoint} for client ${String(activeClientId)}`,
        )
      })

      setIsBootstrapLoaded(true)
    })

    return () => {
      isCancelled = true
    }
  }, [activeClientId, mergeEntityPermissions, user?.user])

  useEffect(() => {
    if (!user?.user || snapshot.status === 'error') {
      return
    }

    const canMarkReady = isAppMenuLoaded && (!activeClientId || isBootstrapLoaded)

    if (!canMarkReady || snapshot.status === 'ready') {
      return
    }

    setSnapshot((current) => {
      if (current.status === 'error' || current.status === 'ready') {
        return current
      }

      return {
        ...current,
        status: 'ready',
        loadedAt: new Date().toISOString(),
      }
    })
  }, [activeClientId, isAppMenuLoaded, isBootstrapLoaded, snapshot.status, user?.user])

  const evaluateSurface = useCallback(
    (surface: PermissionSurface, input?: PermissionEvaluationInput) =>
      evaluatePermissionSurface(snapshot, surface, input),
    [snapshot],
  )

  const value = useMemo<PermissionContextValue>(
    () => ({
      snapshot,
      setAppMenuFeatures,
      setPermissionStatus,
      setEntityPermissions,
      mergeEntityPermissions,
      getEntityPermissions,
      clearClientScope,
      evaluateSurface,
    }),
    [
      clearClientScope,
      evaluateSurface,
      getEntityPermissions,
      mergeEntityPermissions,
      setAppMenuFeatures,
      setEntityPermissions,
      setPermissionStatus,
      snapshot,
    ],
  )

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>
}

export const usePermissionContext = (): PermissionContextValue => {
  const context = React.useContext(PermissionContext)

  if (!context) {
    throw new Error('usePermissionContext must be used within a PermissionProvider')
  }

  return context
}
